'use client';

import { c as _c } from "react/compiler-runtime";
import { usdToTrons } from '@shared/all/domains/products';
import { formatUSD } from '@shared/utils/helpers/formatCurrency';
import { formatNumbers } from '@shared/utils/helpers/formatNumbers';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import useUser from '@shared/utils/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { client } from '../../utils/trpc';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import DonationAstronaut, { getAmountForMood, getDonationMood, RocketSlider } from '../common/DonationAstronaut';

// Gauge thresholds, in dollars of AI the pool can fund. Display only — say it
// in money so the gauge keeps its meaning if the tron basis ever moves.
const MIN_POOL_TRONS = usdToTrons(5);
const MAX_POOL_TRONS = usdToTrons(500);
const HAPPY_POOL_TRONS = usdToTrons(100);
const POOL_STEP = 100_000;
const MIN_DONATION_CENTS = 500;
const MAX_DONATION_CENTS = 5000;
const HAPPY_DONATION_CENTS = 2000;
type DonationMode = 'trons' | 'cash';
type Props = {
  onOpenDonation: (amountCents: number) => void;
};
const CommunityPoolCard = t0 => {
  const $ = _c(62);
  const {
    onOpenDonation
  } = t0;
  const {
    currentUser,
    addTrons
  } = useUser();
  const [donationMode, setDonationMode] = useState("cash");
  const [tronAmount, setTronAmount] = useState(MIN_POOL_TRONS);
  const [cashAmountCents, setCashAmountCents] = useState(MIN_DONATION_CENTS);
  const [submitting, setSubmitting] = useState(false);
  const [showTronConfirm, setShowTronConfirm] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["community-pool-status"],
      queryFn: _temp
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: status,
    isLoading,
    refetch
  } = useQuery(t1);
  const canDonateTrons = currentUser.isAuthenticated && currentUser.tronsBalance >= MIN_POOL_TRONS;
  const maxTrons = Math.min(MAX_POOL_TRONS, currentUser.tronsBalance);
  const effectiveTronAmount = Math.min(tronAmount, maxTrons);
  let t2;
  if ($[1] !== effectiveTronAmount) {
    t2 = getDonationMood(effectiveTronAmount, MIN_POOL_TRONS, HAPPY_POOL_TRONS, MAX_POOL_TRONS);
    $[1] = effectiveTronAmount;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const tronMood = t2;
  let t3;
  if ($[3] !== cashAmountCents) {
    t3 = getDonationMood(cashAmountCents, MIN_DONATION_CENTS, HAPPY_DONATION_CENTS, MAX_DONATION_CENTS);
    $[3] = cashAmountCents;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const cashMood = t3;
  const mood = donationMode === "trons" ? tronMood : cashMood;
  const showTronControls = donationMode === "trons" && canDonateTrons;
  const showTronGate = donationMode === "trons" && !canDonateTrons;
  const showCash = donationMode === "cash";
  if (isLoading || !status) {
    return null;
  }
  let t4;
  if ($[5] !== addTrons || $[6] !== canDonateTrons || $[7] !== currentUser.tronsBalance || $[8] !== effectiveTronAmount || $[9] !== refetch) {
    t4 = async () => {
      if (!canDonateTrons) {
        toast("You need at least 1,000,000 trons to donate to the pool", "", "error");
        return;
      }
      if (effectiveTronAmount > currentUser.tronsBalance) {
        toast("You can't donate more trons than you have", "", "error");
        return;
      }
      setSubmitting(true);
      const [error] = await client.pool.donate.mutate({
        amount: effectiveTronAmount
      });
      setSubmitting(false);
      if (error) {
        return;
      }
      addTrons(-effectiveTronAmount);
      setShowTronConfirm(false);
      toast("Thank you for donating to the community pool!", "", "success");
      refetch();
    };
    $[5] = addTrons;
    $[6] = canDonateTrons;
    $[7] = currentUser.tronsBalance;
    $[8] = effectiveTronAmount;
    $[9] = refetch;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const donateTrons = t4;
  const t5 = donationMode === "trons";
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setDonationMode("trons");
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `flex-1 rounded-md px-3 py-2 text-sm font-emphasis transition-colors ${donationMode === "trons" ? "bg-primary text-white shadow-sm" : "text-textSecondary hover:text-textPrimary"}`;
  let t8;
  if ($[12] !== t5 || $[13] !== t7) {
    t8 = <button type="button" role="tab" aria-selected={t5} onClick={t6} className={t7}>Trons</button>;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = donationMode === "cash";
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setDonationMode("cash");
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const t11 = `flex-1 rounded-md px-3 py-2 text-sm font-emphasis transition-colors ${donationMode === "cash" ? "bg-primary text-white shadow-sm" : "text-textSecondary hover:text-textPrimary"}`;
  let t12;
  if ($[16] !== t11 || $[17] !== t9) {
    t12 = <button type="button" role="tab" aria-selected={t9} onClick={t10} className={t11}>Cash (USD)</button>;
    $[16] = t11;
    $[17] = t9;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t8) {
    t13 = <div className="flex rounded-lg border border-ds-border-standard bg-background-light p-1" role="tablist" aria-label="Donation type">{t8}{t12}</div>;
    $[19] = t12;
    $[20] = t8;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== mood) {
    t14 = <DonationAstronaut mood={mood} />;
    $[22] = mood;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== effectiveTronAmount || $[25] !== maxTrons || $[26] !== showTronControls || $[27] !== tronMood) {
    t15 = showTronControls && <><div className="mb-2 flex items-center justify-between text-sm"><span className="font-emphasis text-textPrimary">Trons to the pool</span><span key={effectiveTronAmount} className="anim-enter text-lg font-semibold text-primary [--enter-scale:1.15]">{formatNumbers(effectiveTronAmount)}</span></div><RocketSlider progress={tronMood} onScrub={m => {
        const raw = getAmountForMood(m, MIN_POOL_TRONS, HAPPY_POOL_TRONS, MAX_POOL_TRONS);
        setTronAmount(Math.min(maxTrons, Math.max(MIN_POOL_TRONS, Math.round(raw / POOL_STEP) * POOL_STEP)));
      }} ariaLabel="Trons to donate to the community pool" valueText={`${formatNumbers(effectiveTronAmount)} trons`} /><div className="mt-1 flex justify-between text-[10px] uppercase tracking-wide text-textSecondary"><span>1M</span><span>20M</span><span>100M</span></div></>;
    $[24] = effectiveTronAmount;
    $[25] = maxTrons;
    $[26] = showTronControls;
    $[27] = tronMood;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== showTronGate) {
    t16 = showTronGate && <p className="text-center text-sm text-textSecondary sm:text-left">Sign in with at least 1,000,000 trons to donate to the community pool.</p>;
    $[29] = showTronGate;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== cashAmountCents || $[32] !== cashMood || $[33] !== showCash) {
    t17 = showCash && <><div className="mb-2 flex items-center justify-between text-sm"><span className="font-emphasis text-textPrimary">Donation amount</span><span key={cashAmountCents} className="anim-enter text-lg font-semibold text-primary [--enter-scale:1.15]">{formatUSD(cashAmountCents)}</span></div><RocketSlider progress={cashMood} onScrub={m_0 => {
        const raw_0 = getAmountForMood(m_0, MIN_DONATION_CENTS, HAPPY_DONATION_CENTS, MAX_DONATION_CENTS);
        setCashAmountCents(Math.min(MAX_DONATION_CENTS, Math.max(MIN_DONATION_CENTS, Math.round(raw_0 / 100) * 100)));
      }} ariaLabel="Cash donation amount" valueText={formatUSD(cashAmountCents)} /><div className="mt-1 flex justify-between text-[10px] uppercase tracking-wide text-textSecondary"><span>$5</span><span>$20</span><span>$50</span></div></>;
    $[31] = cashAmountCents;
    $[32] = cashMood;
    $[33] = showCash;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t15 || $[36] !== t16 || $[37] !== t17) {
    t18 = <div className="w-full flex-1">{t15}{t16}{t17}</div>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t14 || $[40] !== t18) {
    t19 = <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-5">{t14}{t18}</div>;
    $[39] = t14;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== effectiveTronAmount || $[43] !== showTronControls || $[44] !== status || $[45] !== submitting) {
    t20 = showTronControls && <><div className="text-sm text-textSecondary"><p>Trons help fuel community research —{" "}{formatNumbers(status.balance)} pooled so far.</p>{status.mode !== "OFF" && status.myAccess && <p className="mt-2 font-emphasis text-primary">You can draw from the pool —{" "}{formatNumbers(status.myRemaining4h)} of{" "}{formatNumbers(status.userCapPer4h)} trons left in this 4h window.</p>}{status.mode === "WHITELIST" && !status.myAccess && <p className="mt-2">Pool spending is currently limited to whitelisted members.</p>}</div><Button text={`Donate ${formatNumbers(effectiveTronAmount)} trons`} onClick={() => setShowTronConfirm(true)} disabled={submitting} /></>;
    $[42] = effectiveTronAmount;
    $[43] = showTronControls;
    $[44] = status;
    $[45] = submitting;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== cashAmountCents || $[48] !== onOpenDonation || $[49] !== showCash) {
    t21 = showCash && <><p className="text-sm text-textSecondary">Your donation helps keep Factiii running and growing.</p><Button text={`Donate ${formatUSD(cashAmountCents)}`} onClick={() => onOpenDonation(cashAmountCents)} /></>;
    $[47] = cashAmountCents;
    $[48] = onOpenDonation;
    $[49] = showCash;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== donateTrons || $[52] !== effectiveTronAmount || $[53] !== showTronConfirm || $[54] !== submitting) {
    t22 = showTronConfirm && <Confirmation title="Donate to the community pool?" message={`This deducts ${formatNumbers(effectiveTronAmount)} trons from your balance and adds them to the shared pool.`} actionButtonText={`Donate ${formatNumbers(effectiveTronAmount)} trons`} confirmHandler={donateTrons} cancelHandler={() => setShowTronConfirm(false)} actionButtonLoading={submitting} />;
    $[51] = donateTrons;
    $[52] = effectiveTronAmount;
    $[53] = showTronConfirm;
    $[54] = submitting;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t13 || $[57] !== t19 || $[58] !== t20 || $[59] !== t21 || $[60] !== t22) {
    t23 = <div className="anim-enter flex flex-col gap-4 rounded-xl border border-solid border-ds-border-standard bg-background-secondary p-4 [--enter-duration:400ms] [--enter-y:20px] md:p-6">{t13}{t19}{t20}{t21}{t22}</div>;
    $[56] = t13;
    $[57] = t19;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  return t23;
};
export default CommunityPoolCard;
function _temp() {
  return unwrapFromResult(client.pool.status.query());
}